import { Space, Button, Popconfirm, message, Input } from "antd";
import Table, { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { UIAssetFilterGroupDTO } from "../../backendapi/requests";
import { useContext, useMemo, useState } from "react";
import { CheckCircleOutlined, PlusOutlined, SearchOutlined, StopOutlined } from "@ant-design/icons";
import { useDebounce } from "use-debounce";
import { Filter } from "../../types/types";
import { useQueryClient } from "@tanstack/react-query";
import { ConfigContext } from "../../contexts/ConfigContext";
import { useUiAssetFilterServiceDeleteBackendv2Uiassetfiltergroup, useUiAssetFilterServiceGetBackendv2Uiassetfiltergroup, useUiAssetFilterServiceGetBackendv2UiassetfiltergroupKey } from "../../backendapi/queries";
import { GenerateODataFilter } from "../../helpers/odataFunctions";
import CreateUIAssetFilterGroup from "./CreateUIAssetFilterGroup";
import EditUIAssetFilterGroup from "./EditUIAssetFilterGroup";

const UIAssetFilterGroupList: React.FC<{}> = () => {
  const queryClient = useQueryClient();

  const { currentClient } = useContext(ConfigContext);

  const [messageApi, contextHolder] = message.useMessage();
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebounce(query, 500);

  const [isCreateDrawerVisible, setIsCreateDrawerVisible] = useState(false);
  const [isEditDrawerVisible, setIsEditDrawerVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState<UIAssetFilterGroupDTO | null>(null);
  const [sortField, setSortField] = useState<string>("id");
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showTotal: (total) => `Total ${total} items`,
  });

  const filters = useMemo(() => {
    let filters: Filter[] = [];

    if (debouncedQuery) {
      filters.push({ type: "search", values: [{ value: debouncedQuery }], property: "title", name: "name" });
    }

    return filters;
  }, [debouncedQuery, currentClient]);

  const { data: uiassetfiltergroups, isFetching: loading } = useUiAssetFilterServiceGetBackendv2Uiassetfiltergroup(
    {
      clientId: currentClient.id,
      top: pagination.pageSize?.toString(),
      skip: (((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 1)).toString(),
      filter: GenerateODataFilter(filters),
      orderby: `${sortField} ${sortOrder}`,    
      count: "true",
    },
    [useUiAssetFilterServiceGetBackendv2UiassetfiltergroupKey, currentClient.id, filters],
    { refetchOnWindowFocus: false, placeholderData: (prev) => prev }
  );

  const { mutateAsync: deleteUIAssetFilterGroup } = useUiAssetFilterServiceDeleteBackendv2Uiassetfiltergroup();

  const handleTableChange = async (newPagination: TablePaginationConfig) => {
    await queryClient.invalidateQueries({ queryKey: [useUiAssetFilterServiceGetBackendv2UiassetfiltergroupKey] });
    setPagination({ ...newPagination, total: uiassetfiltergroups?.value.length });
  };

  const handleSearch = async (value: string) => {
    setQuery(value);
  };

  const showEditDrawer = (group: UIAssetFilterGroupDTO) => {
    setEditingGroup(group);
    setIsEditDrawerVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteUIAssetFilterGroup({ key: id });

      messageApi.success("User group deleted");
      await queryClient.invalidateQueries({ queryKey: [useUiAssetFilterServiceGetBackendv2UiassetfiltergroupKey] });
    } catch (error) {
      messageApi.error("Failed to delete user group");
    }
  };

  const handleSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: [useUiAssetFilterServiceGetBackendv2UiassetfiltergroupKey] });
  };

  const columns: ColumnsType<UIAssetFilterGroupDTO> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: (a, b) => a.title.localeCompare(b.title),
    },
    {
      title: "Assets Enabled",
      dataIndex: "assetsenabled",
      key: "enabledForAssets",
       align: "center",  
      render: (value, record, index) => (record.enabledForAssets ? <CheckCircleOutlined style={{ color: '#389e0d' }} /> : <StopOutlined style={{ color: '#cf1322' }} />),
    },
    {
      title: "Templates Enabled",
      dataIndex: "templatesenabled",
      key: "enabledForTemplates",   
      align: "center",  
      render: (value, record, index) => (record.enabledForTemplates ? <CheckCircleOutlined style={{ color: '#389e0d' }} /> : <StopOutlined style={{ color: '#cf1322' }} />),
    },
    {
      title: "Configuration Enabled",
      dataIndex: "configurationenabled",
      key: "enabledForConfiguration",
       align: "center",  
      render: (value, record, index) => (record.enabledForConfiguration ? <CheckCircleOutlined style={{ color: '#389e0d' }} /> : <StopOutlined style={{ color: '#cf1322' }} />),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => showEditDrawer(record)}>
            Edit
          </Button>
          <Popconfirm title="Are you sure you want to delete this user group?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
            <Button type="link">Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Space>
            <Input placeholder="Search" prefix={<SearchOutlined />} allowClear style={{ maxWidth: 400 }} onChange={(e) => handleSearch(e.target.value)} />
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreateDrawerVisible(true)}>
            Add AssetFilter Group
          </Button>
        </div>

        <Table columns={columns} dataSource={uiassetfiltergroups?.value} rowKey="id" loading={loading} pagination={pagination} onChange={handleTableChange} scroll={{ x: 1000 }} />

        <CreateUIAssetFilterGroup visible={isCreateDrawerVisible} onClose={() => setIsCreateDrawerVisible(false)} onSuccess={handleSuccess} />

        {editingGroup && (
          <EditUIAssetFilterGroup
            visible={isEditDrawerVisible}
            onClose={() => {
              setIsEditDrawerVisible(false);
              setEditingGroup(null);
            }}
            onSuccess={handleSuccess}
            filterGroup={editingGroup}            
          />
        )}
      </Space>
    </>
  );
};

export default UIAssetFilterGroupList;
